/**
 * nw-popups-group.js - 弹出窗口组件库
 * 
 * 一、nw-modal 自定义元素
 * 属性：id (必填), open (可选)
 * 方法：show(), hide()
 * 使用：<nw-modal id="my-modal"><div class="overlay">...</div></nw-modal>
 * 控制按钮需使用 aria-controls 属性指向模态框 id
 * 
 * 二、initPopupTabs 函数
 * 参数：
 *   - sectionRoot: HTMLElement (必填) - 包含标签页的根容器
 *   - defaultTarget: string|null (可选) - 默认激活的标签 ID
 * 
 * 三、自动初始化
 * 脚本会自动初始化所有 .nw-popup-modal 元素中的标签页功能
 */

(function () {
  // 防止重复定义
  if (customElements.get('nw-modal')) return;

  class NwModal extends HTMLElement {
    static get observedAttributes() {
      return ['open', 'id'];
    }

    constructor() {
      super();
      this.abortController = null;
    }

    get overlay() {
      return this.querySelector('.overlay, [class*="overlay"]');
    }

    get controls() {
      if (!this.id) return [];
      return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get open() {
      return this.hasAttribute('open');
    }

    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      // 绑定控制按钮点击事件
      this.controls.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.open ? this.hide() : this.show();
        }, { signal });
      });

      document.addEventListener('keyup', (e) => {
        if (e.code === 'Escape' && this.open) {
          this.hide();
        }
      }, { signal });

      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay) {
            this.hide();
          }
        }, { signal });
      }
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'open') {
        // 更新控制按钮的 aria-expanded
        this.controls.forEach(button => {
          button.setAttribute('aria-expanded', newValue !== null ? 'true' : 'false');
        });

        // 显示/隐藏逻辑
        if (oldValue === null && newValue !== null) {
          // 打开
          this.hidden = false;
          this.removeAttribute('inert');
          
          // 移动到 body（如果不在 body 中）
          if (this.parentElement !== document.body) {
            this.originalParent = this.parentElement;
            document.body.appendChild(this);
          }

          // 阻止 body 滚动
          document.body.classList.add('has-modal-open');

          // 添加 active 类以触发动画
          requestAnimationFrame(() => {
            this.setAttribute('active', '');
            // 初始化弹窗内的 nw-tab-scroller
            this.initTabScrollers();
          });
        } else if (oldValue !== null && newValue === null) {
          // 关闭
          this.setAttribute('inert', '');
          this.removeAttribute('active');

          // 恢复 body 滚动
          document.body.classList.remove('has-modal-open');

          // 等待动画完成后隐藏
          if (this.overlay) {
            this.overlay.addEventListener('transitionend', () => {
              if (!this.hasAttribute('open')) {
                this.hidden = true;
                // 恢复原始位置
                if (this.originalParent && this.parentElement === document.body) {
                  this.originalParent.appendChild(this);
                  this.originalParent = null;
                }
              }
            }, { once: true });
          } else {
            this.hidden = true;
            if (this.originalParent && this.parentElement === document.body) {
              this.originalParent.appendChild(this);
              this.originalParent = null;
            }
          }
        }
      }
    }

    show() {
      if (this.open) return;
      this.setAttribute('open', '');
    }

    hide() {
      if (!this.open) return;
      this.removeAttribute('open');
    }

    // 初始化弹窗内的 nw-tab-scroller
    initTabScrollers() {
      const tabScrollers = this.querySelectorAll('nw-tab-scroller');
      tabScrollers.forEach(scroller => {
        // 如果还没初始化，强制初始化
        if (!scroller.isInitialized) {
          // 重置标记，允许重新初始化
          scroller.dataset.initialized = 'false';
          scroller.isInitialized = false;
          // 触发初始化
          if (scroller.tryInit) {
            scroller.tryInit();
          } else {
            // 如果没有 tryInit 方法，直接调用 init
            requestAnimationFrame(() => {
              scroller.init();
            });
          }
        }
      });
    }
  }

  customElements.define('nw-modal', NwModal);

  // 初始化 Popup Tabs
  function initPopupTabs(sectionRoot, defaultTarget = null) {
    if (!sectionRoot) return;

    const tabItems = sectionRoot.querySelectorAll('.tab-item');
    const tabContents = sectionRoot.querySelectorAll('.popup-tab-content');

    function activateTab(item) {
      const target = item.dataset.target;

      tabItems.forEach(i => i.classList.remove('active'));
      item.classList.add('active');

      tabContents.forEach(c => c.classList.add('hidden'));
      sectionRoot.querySelector(`#${target}`)?.classList.remove('hidden');
    }

    // 绑定 tab 点击（防重复）
    tabItems.forEach(item => {
      item.addEventListener('click', () => activateTab(item));
    });

    // 初始化 tab
    const defaultItem =
      defaultTarget
        ? sectionRoot.querySelector(`.tab-item[data-target="${defaultTarget}"]`)
        : sectionRoot.querySelector('.tab-item.active') || tabItems[0];

    defaultItem && activateTab(defaultItem);

    // 箭头逻辑已由 nw-tab-scroller 组件处理
  }

  // 初始化所有弹窗 tabs
  function initAllPopupTabs() {
    const sectionRoots = document.querySelectorAll('.nw-popup-modal');
    sectionRoots.forEach(sectionRoot => {
      initPopupTabs(sectionRoot);
    });
  }

  // DOMContentLoaded 时初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPopupTabs);
  } else {
    initAllPopupTabs();
  }
})();
